import type { AnalyticsCaptureOptions } from 'src/modules/analytics/posthogClient';
import type { ReportErrorContext } from 'src/modules/logs/Sentry';
import type {
  ReloadCoverLiftReason,
  ReloadCoverSource,
} from 'src/modules/splash-screen';
import type { BootPhase } from './bootPhase';
import { buildAttemptContext, resolveAttemptFailure } from './attemptFailure';
import { denyBundle } from './bundleDenylist';
import type { CapgoReasonClass } from './capgoReason';
import { snapshotCapgoLines, type CapgoRingEntry } from './capgoConsoleRing';
import { decideRecovery, type CrashState } from './crashRecovery';
import type { DeviceDiagnostics, NetworkConnectionType } from './deviceDiagnostics';
import { classifyGateHint, type GateHint } from './gateHint';
import type { RenderHealthState } from './renderHealth';
import type { RevertReason } from './revertReport';
import {
  computeShouldUpdateNativeApp,
  decideUpdate,
  type DecideUpdateInput,
  type UpdateDecision,
} from './updateCheck';
import { runUpdate, type RunUpdateResult, type UpdateRunnerDeps } from './updateDownload';

export interface GateState {
  percent: number;
  isOffline: boolean;
  // `false` until a REAL native download tick has arrived, as opposed to the synthetic
  // `onProgress(0)` `updateDownload.ts` fires at the start of every attempt.
  hasProgress: boolean;
  // One classified line under the bar or the offline screen; survives progress ticks and the
  // stall watchdog and is replaced by the next failure's classification, except disk_full, which
  // sticks for the gate.
  hint: GateHint | null;
}

export type GateUpdate =
  | GateState
  | null
  | ((previous: GateState | null) => GateState | null);

// `halt` means the caller must NOT render: either a reload/reset is in flight and the webview is
// being torn down, or the update gate is up and owns the screen.
export type BootOutcome = 'continue' | 'halt';

export type UpdateEventName = 'app_update_finished' | 'app_update_failed';

export type TimerKind =
  | 'backoff'
  | 'stall_watchdog'
  | 'pre_ready_read'
  | 'apply_grace'
  | 'update_check'
  | 'download_failed_grace';

// How long the gate waits, after `set()` has committed and `reload()` has been requested, before
// concluding the reload never took effect and showing a control.
// Delay needed because flipping to the offline state the instant reload() is requested would swap the
// success screen for the failure screen on every update; reloads commit in 50-300ms, so 5s never shows on success.
export const APPLY_GRACE_MS = 5_000;

// How long the gate tolerates silence from the native downloader before giving the user a way out.
// Android's native download has no timeout and can never settle, so only a watchdog frees the user. Gap
// between progress ticks, not total time: false positives abandon a download, false negatives cost 90s.
export const DOWNLOAD_STALL_MS = 90_000;

// The Android `downloadFailed` event and the download rejection are emitted in the same native millisecond,
// but the JS bridge delivers the event up to ~60 ms before or after the rejection.
export const DOWNLOAD_FAILED_GRACE_MS = 150;
const DOWNLOAD_FAILED_POLL_MS = 25;

// Cap on each of the two Capgo reads that run BEFORE `notifyAppReady()`.
export const PRE_READY_READ_TIMEOUT_MS = 2_000;

// Cap on `gatherUpdateInput()` — the pre-decision read that stands between boot and the
// download-vs-continue fork.
export const UPDATE_CHECK_TIMEOUT_MS = 30_000;

export interface BootSequenceDeps {
  isNative: boolean;
  isDev: boolean;
  now: () => number;

  setBootPhase: (phase: BootPhase) => void;

  getFailedUpdate: () => Promise<{ bundle?: { id?: string } } | null>;
  getCurrentBundleId: () => Promise<string>;
  getCurrentBundleVersion: () => Promise<string>;
  readCrashState: () => CrashState | null;
  recordLaunch: (now: number) => void;
  clearCrashState: () => void;
  installBackgroundClear: () => void;
  readRenderHealth: () => RenderHealthState | null;
  clearRenderHealth: () => void;
  noteActiveBundleId: (id: string) => void;
  persistRevertReport: (reason: RevertReason, deniedVersion: string) => void;
  resetToLastSuccessful: () => Promise<unknown>;
  resetToBuiltin: () => Promise<unknown>;
  notifyAppReady: () => Promise<unknown>;

  unregisterServiceWorkers: () => void;
  showSplash: () => Promise<unknown>;
  // Must run the operation even when the cover fails, and lift the cover if the operation rejects.
  withReloadCover: <T>(
    operation: () => Promise<T>,
    source: ReloadCoverSource,
  ) => Promise<T>;
  liftReloadCover: (reason: ReloadCoverLiftReason) => Promise<unknown>;
  hideSplash: () => Promise<unknown>;
  nextPaint: () => Promise<void>;

  runLocalInit: () => Promise<void>;

  gatherUpdateInput: () => Promise<DecideUpdateInput>;
  addDownloadListener: (
    onPercent: (percent: number) => void,
  ) => Promise<(() => Promise<unknown>) | null>;
  download: UpdateRunnerDeps['download'];
  set: UpdateRunnerDeps['set'];
  reload: UpdateRunnerDeps['reload'];
  getMissingBundleFiles: UpdateRunnerDeps['getMissingBundleFiles'];
  isProgressiveTrigger: UpdateRunnerDeps['isProgressiveTrigger'];
  cleanupFailedBundles?: UpdateRunnerDeps['cleanupFailedBundles'];
  readProgressiveHint: UpdateRunnerDeps['readProgressiveHint'];
  writeProgressiveHint: UpdateRunnerDeps['writeProgressiveHint'];
  readDeviceDiagnostics: () => Promise<DeviceDiagnostics>;
  // Live connection type for events fired later in the gate; resolves to the remover, or null when unavailable.
  watchConnectionType: (
    onChange: (type: NetworkConnectionType) => void,
  ) => Promise<(() => unknown) | null>;
  schedule: (fn: () => void, ms: number, kind: TimerKind) => () => void;
  setRetryAction: (action: (() => void) | null) => void;

  setShouldUpdateNativeApp: (value: boolean) => void;
  setGate: (update: GateUpdate) => void;

  reportError: (message: string, error?: unknown, context?: ReportErrorContext) => void;
  // Named `logAnalyticsEvent` deliberately, and destructured to a bare local at every use site.
  // Named `logAnalyticsEvent` and destructured bare at every use: extract-analytics-inventory.ts only
  // parses bare calls, so a property access would silently drop these events from the catalog.
  logAnalyticsEvent: (
    name: UpdateEventName,
    params: Record<string, unknown>,
    options?: AnalyticsCaptureOptions,
  ) => void;
}

export type BootView =
  | { kind: 'gate'; gate: GateState }
  | { kind: 'blank' }
  | { kind: 'app' };

// Order is load-bearing: testing isReady before gate type-checks but leaves a blank screen during the update.
export const pickBootView = (state: {
  gate: GateState | null;
  isReady: boolean;
}): BootView => {
  if (state.gate) return { kind: 'gate', gate: state.gate };
  return state.isReady ? { kind: 'app' } : { kind: 'blank' };
};

export const shouldReportAttempt = (attempt: number): boolean =>
  attempt < 3 || attempt % 10 === 0;

// Builtin fallback: with no last-successful bundle the first reset fails every launch, a blank-screen loop.
const revertBundle = async (
  deps: Pick<BootSequenceDeps, 'resetToLastSuccessful' | 'resetToBuiltin' | 'reportError'>,
): Promise<void> => {
  try {
    await deps.resetToLastSuccessful();
  } catch (error) {
    deps.reportError(
      'Capgo revert to last-successful failed, falling back to builtin',
      error,
    );
    try {
      await deps.resetToBuiltin();
    } catch (fallbackError) {
      deps.reportError('Capgo revert to builtin failed', fallbackError);
    }
  }
};

const capPreReadyRead = <T>(
  deps: Pick<BootSequenceDeps, 'schedule'>,
  work: Promise<T>,
  fallback: T,
): Promise<T> =>
  new Promise<T>((resolve) => {
    const cancel = deps.schedule(
      () => resolve(fallback),
      PRE_READY_READ_TIMEOUT_MS,
      'pre_ready_read',
    );
    work.then(
      (value) => {
        cancel();
        resolve(value);
      },
      () => {
        cancel();
        resolve(fallback);
      },
    );
  });

export type CrashRecoveryDeps = Pick<
  BootSequenceDeps,
  | 'isNative'
  | 'now'
  | 'schedule'
  | 'getFailedUpdate'
  | 'getCurrentBundleId'
  | 'getCurrentBundleVersion'
  | 'readCrashState'
  | 'recordLaunch'
  | 'clearCrashState'
  | 'clearRenderHealth'
  | 'noteActiveBundleId'
  | 'persistRevertReport'
  | 'readRenderHealth'
  | 'resetToLastSuccessful'
  | 'resetToBuiltin'
  | 'notifyAppReady'
  | 'reportError'
>;

export const runCrashRecovery = async (
  deps: CrashRecoveryDeps,
): Promise<BootOutcome> => {
  if (!deps.isNative) return 'continue';

  // getFailedUpdate() resolves to `UpdateFailedEvent | null`, i.e. `{ bundle: BundleInfo }`, so the id is
  // one level down: `failed?.id` is silently undefined. Keep these reads before notifyAppReady().
  const failed = await capPreReadyRead<{ bundle?: { id?: string } } | null>(
    deps,
    deps.getFailedUpdate(),
    null,
  );
  const currentBundleId = await capPreReadyRead(deps, deps.getCurrentBundleId(), '');
  // Feeds armRenderProbe (called much later, right before setIsReady(true)) so it can tell "this
  // launch" from "a different bundle's leftover count".
  deps.noteActiveBundleId(currentBundleId);
  const decision = decideRecovery({
    failedBundleId: failed?.bundle?.id ?? null,
    currentBundleId,
    state: deps.readCrashState(),
    renderHealth: deps.readRenderHealth(),
    now: deps.now(),
  });

  if (decision.kind === 'revert') {
    deps.clearCrashState();
    deps.clearRenderHealth();
    deps.reportError(`Reverting Capgo bundle: ${decision.reason}`);
    // Persist immediately: this population force-quits every 1-3s and the version read can stall.
    deps.persistRevertReport(decision.reason, '');
    // Denylist the bundle we are escaping: it is still on disk with a usable status — it DID call
    // notifyAppReady() before dying — so without this the very next launch finds it via
    // decideUpdate's installed-bundle reuse and re-applies it, recreating the loop this revert
    // exists to break.
    const escapedVersion = await capPreReadyRead(
      deps,
      deps.getCurrentBundleVersion(),
      '',
    );
    if (escapedVersion) {
      denyBundle(escapedVersion, deps.now());
      deps.persistRevertReport(decision.reason, escapedVersion);
    }
    // Reverting to the last KNOWN-GOOD bundle rather than builtin: builtin maximises blast radius
    // and guarantees a full re-download.
    void revertBundle(deps);
    return 'halt';
  }

  deps.recordLaunch(deps.now());
  // Tell Capgo the bundle loaded.
  await deps.notifyAppReady().catch((error) => {
    deps.reportError('Capgo notifyAppReady failed', error);
  });
  return 'continue';
};

// Clears the crash counter the moment local init has been proven to work.
export const clearCrashStateIfLocalInitSucceeded = (
  deps: Pick<BootSequenceDeps, 'isNative' | 'clearCrashState' | 'installBackgroundClear'>,
  localInitSucceeded: boolean,
): void => {
  if (!deps.isNative || !localInitSucceeded) return;
  deps.clearCrashState();
  deps.installBackgroundClear();
};

interface SequenceState {
  gateRaised: boolean;
}

// The Network Information API (`navigator.connection`) is Chromium-only, so this reads as
// Android-only in practice — WKWebView has no such property; iOS falls back to the native
// `Network` plugin: the live status-change value, else the gate-start diagnostics read.
const readConnectionType = (): string | undefined =>
  (navigator as Navigator & { connection?: { effectiveType?: string } }).connection
    ?.effectiveType;

const OFFLINE_AFTER_FAILURES = 3;

const runGate = async (
  deps: BootSequenceDeps,
  state: SequenceState,
  decision: Extract<UpdateDecision, { kind: 'download' | 'apply_installed' }>,
  input: DecideUpdateInput,
  startedAt: number,
): Promise<BootOutcome> => {
  const { logAnalyticsEvent } = deps;
  // FIRST statement, before any call that could throw: from here on the decision to update is
  // taken, and runBootSequence's catch must fail CLOSED. setBootPhase is total today, so ordering
  // these the other way is only theoretically wrong — but the invariant should not depend on
  // another module staying total.
  state.gateRaised = true;
  let diagnostics: DeviceDiagnostics = {};
  let diagnosticsRead: Promise<void> = Promise.resolve();
  let liveConnectionType: NetworkConnectionType | undefined;
  let removeConnectionWatch: (() => unknown) | null = null;
  // Read at event time: both iOS sources resolve after the gate starts, so an eager read would always miss them.
  const currentConnectionType = (): string | undefined =>
    readConnectionType() ?? liveConnectionType ?? diagnostics.connectionType;
  let lastProgressPercent: number | undefined;
  let lastAttemptStartedAt: number | undefined;
  let gateHint: GateHint | null = null;
  let consecutiveFailures = 0;
  let failureBaseline: number | undefined;
  let previousAttemptAt: number | undefined;
  deps.setBootPhase('downloading');
  deps.setGate({ percent: 0, isOffline: false, hasProgress: false, hint: null });

  // Capgo's set() already reloads the WebView, so it carries the cover and the reload that
  // follows it rides on the same one; a retry reload has no set() before it and covers itself.
  const coveredSet: BootSequenceDeps['set'] = (options) =>
    deps.withReloadCover(() => deps.set(options), 'gate_set');
  const coveredReload: BootSequenceDeps['reload'] = () =>
    deps.withReloadCover(() => deps.reload(), 'gate_retry');
  // The retry control sits under the cover until it is lifted.
  const liftCover = (reason: ReloadCoverLiftReason) => {
    void deps.liftReloadCover(reason).catch(() => {});
  };

  const wait = (ms: number): Promise<void> =>
    new Promise<void>((resolve) => {
      let cancel = () => {};
      const finish = () => {
        deps.setRetryAction(null);
        resolve();
      };
      // Installed BEFORE scheduling so the two orderings agree even if `schedule` fires
      // synchronously; otherwise a stale action would outlive its wait.
      deps.setRetryAction(() => {
        cancel();
        deps.setGate((g) => ({
          percent: g?.percent ?? 0,
          isOffline: false,
          hasProgress: false,
          hint: g?.hint ?? null,
        }));
        finish();
      });
      cancel = deps.schedule(finish, ms, 'backoff');
    });

  // Deliberately does NOT abort or re-issue the native download: with no `isVersionDownloading`
  // dedupe a second download() would run CONCURRENTLY with the first.
  let cancelStallTimer: (() => void) | null = null;
  let stallReported = false;

  const cancelStallWatchdog = () => {
    cancelStallTimer?.();
    cancelStallTimer = null;
  };

  const armStallWatchdog = () => {
    cancelStallWatchdog();
    cancelStallTimer = deps.schedule(
      () => {
        cancelStallTimer = null;
        liftCover('gate_stall');
        deps.setRetryAction(() => {
          void coveredReload();
        });
        // 90 s of silence is the slow-link signature (Android never fails a slow download);
        // an actionable disk-full line is not replaced by it.
        gateHint = gateHint === 'disk_full' ? 'disk_full' : 'low_network';
        const hint = gateHint;
        deps.setGate((g) => ({
          percent: g?.percent ?? 0,
          isOffline: true,
          hasProgress: g?.hasProgress ?? false,
          hint,
        }));
        // Once per gate.
        if (stallReported) return;
        stallReported = true;
        deps.reportError(`OTA download stalled: no progress for ${DOWNLOAD_STALL_MS}ms`);
        logAnalyticsEvent('app_update_failed', {
          failureReason: 'download_stalled',
          durationMs: deps.now() - startedAt,
          fromVersion: input.currentVersion,
          toVersion: decision.version,
          attemptDurationMs:
            lastAttemptStartedAt === undefined ? undefined : deps.now() - lastAttemptStartedAt,
          lastProgressPercent,
          connectionType: currentConnectionType(),
          batteryLevel: diagnostics.batteryLevel,
          isCharging: diagnostics.isCharging,
        });
      },
      DOWNLOAD_STALL_MS,
      'stall_watchdog',
    );
  };

  const applyFailureToGate = (
    reason: CapgoReasonClass,
    { probe, attemptDurationMs, filesMissingAfter }: RunUpdateResult,
  ) => {
    failureBaseline ??= probe?.filesMissing;
    const progressed =
      filesMissingAfter !== undefined &&
      failureBaseline !== undefined &&
      filesMissingAfter < failureBaseline;
    if (filesMissingAfter !== undefined && (progressed || failureBaseline === undefined)) {
      failureBaseline = filesMissingAfter;
    }
    consecutiveFailures = progressed ? 1 : consecutiveFailures + 1;
    // Freeing space takes the user longer than a retry, so disk_full outlives later generic failures.
    gateHint =
      gateHint === 'disk_full'
        ? 'disk_full'
        : classifyGateHint({ reason, consecutiveFailures, attemptDurationMs, lastProgressPercent });
    const hint = gateHint;
    const isOffline = consecutiveFailures >= OFFLINE_AFTER_FAILURES;
    deps.setGate((g) => ({
      percent: g?.percent ?? 0,
      isOffline,
      hasProgress: g?.hasProgress ?? false,
      hint,
    }));
  };

  const rereadAfterGrace = async (error: unknown, since: number | undefined) => {
    let latest: {
      lines: CapgoRingEntry[];
      readAt: number;
      resolved: ReturnType<typeof resolveAttemptFailure>;
    } | null = null;
    try {
      for (let waited = 0; waited < DOWNLOAD_FAILED_GRACE_MS; waited += DOWNLOAD_FAILED_POLL_MS) {
        await new Promise<void>((resolve) => {
          deps.schedule(resolve, DOWNLOAD_FAILED_POLL_MS, 'download_failed_grace');
        });
        const readAt = Date.now();
        const lines = snapshotCapgoLines({ since, now: readAt });
        const resolved = resolveAttemptFailure(error, lines);
        latest = { lines, readAt, resolved };
        if (resolved.reason.reasonClass !== 'android_no_reason') break;
      }
    } catch {
      // the grace read is best-effort and must never fail the attempt
    }
    return latest;
  };

  const onNativeProgress = (percent: number) => {
    lastProgressPercent = Math.round(percent);
    armStallWatchdog();
    deps.setGate({ percent, isOffline: false, hasProgress: true, hint: gateHint });
  };
  const onAttemptReset = (percent: number) => {
    lastProgressPercent = undefined;
    lastAttemptStartedAt = deps.now();
    armStallWatchdog();
    deps.setGate({ percent, isOffline: false, hasProgress: false, hint: gateHint });
  };

  try {
    // Let React paint the gate before the native splash is torn down.
    // rAF is suspended in a webview backgrounded at launch, so do not put blocking work behind this await.
    await deps.nextPaint();

    // Started after first paint so the native bridge call cannot delay it; applied never awaits it.
    diagnosticsRead = deps.readDeviceDiagnostics().then(
      (value) => {
        diagnostics = value;
      },
      () => {},
    );

    // Armed HERE, not before the await above.
    // Armed after the await: before it, a launch backgrounded the whole time would report a stall for a
    // download that never started, clustering at exactly 90s.
    armStallWatchdog();

    // An installed bundle has no progress to show: keep the splash (or its launch hold) up so
    // it is still there when set() reloads, and only lift it if that fails.
    if (decision.kind === 'download') {
      await deps.hideSplash().catch(() => {});
    }

    // Listener registration must not be able to reject: a throw here would strand the user on a 0%
    // gate that never progresses.
    const removeListener = await deps
      .addDownloadListener(onNativeProgress)
      .catch(() => null);

    // Same guard, and a sync throw must not escape either: shells built before @capacitor/network shipped reject here.
    removeConnectionWatch = await Promise.resolve()
      .then(() =>
        deps.watchConnectionType((type) => {
          liveConnectionType = type;
        }),
      )
      .catch(() => null);

    await runUpdate(decision, {
      download: deps.download,
      set: coveredSet,
      reload: deps.reload,
      getMissingBundleFiles: deps.getMissingBundleFiles,
      isProgressiveTrigger: deps.isProgressiveTrigger,
      cleanupFailedBundles: deps.cleanupFailedBundles,
      readProgressiveHint: deps.readProgressiveHint,
      writeProgressiveHint: deps.writeProgressiveHint,
      reportError: deps.reportError,
      // The probe and cleanup tail can outlast the stall window with no native tick.
      onProbe: armStallWatchdog,
      now: deps.now,
      onProgress: onAttemptReset,
      // Fires after the download and BEFORE set() — see UpdateRunnerDeps.onApplied. The delivery
      // options skip PostHog's batch queue and hand the request to the browser, so it outlives the
      // document the WebView swap tears down.
      onApplied: ({ updateMode, fallbackReason, probe, attempt, attemptDurationMs }) => {
        logAnalyticsEvent(
          'app_update_finished',
          {
            outcome: 'updated',
            durationMs: deps.now() - startedAt,
            fromVersion: input.currentVersion,
            toVersion: decision.version,
            updateMode,
            fallbackReason,
            filesTotal: probe?.filesTotal,
            filesMissing: probe?.filesMissing,
            probeMs: probe?.probeMs,
            bytesTotal: probe?.bytesTotal ?? null,
            bytesMissing: probe?.bytesMissing ?? null,
            downloadAttempts: attempt + 1,
            connectionType: currentConnectionType(),
            attemptDurationMs,
            lastProgressPercent,
            batteryLevel: diagnostics.batteryLevel,
            isCharging: diagnostics.isCharging,
          },
          { send_instantly: true, transport: 'sendBeacon' },
        );
      },
      onAttemptFailed: async (attempt, error, telemetry) => {
        const { updateMode, fallbackReason, probe, attemptDurationMs, filesMissingAfter } =
          telemetry;
        // Synchronous and first: the ring is Date.now()-stamped, and unsampled attempts must still advance the window.
        const snapshotAt = Date.now();
        const since = previousAttemptAt;
        let lines = snapshotCapgoLines({ since, now: snapshotAt });
        previousAttemptAt = snapshotAt;
        let { reason, failureDetail } = resolveAttemptFailure(error, lines);
        if (reason.reasonClass === 'android_no_reason') {
          const late = await rereadAfterGrace(error, since);
          if (late) {
            ({ reason, failureDetail } = late.resolved);
            lines = late.lines;
            previousAttemptAt = Math.max(snapshotAt, late.readAt);
          }
        }
        applyFailureToGate(reason.reasonClass, telemetry);
        if (!shouldReportAttempt(attempt)) return;
        deps.reportError(
          `OTA update attempt ${attempt} failed`,
          error,
          buildAttemptContext({
            attempt,
            updateMode: updateMode ?? decision.kind,
            toVersion: decision.version,
            reason,
            lines,
          }),
        );
        const durationMs = deps.now() - startedAt;
        const failedProgressPercent = lastProgressPercent;
        // runUpdate awaits this so a late read cannot let the next attempt apply and swap first.
        await diagnosticsRead;
        logAnalyticsEvent('app_update_failed', {
          failureReason: 'download_failed',
          durationMs,
          fromVersion: input.currentVersion,
          toVersion: decision.version,
          updateMode,
          fallbackReason,
          filesTotal: probe?.filesTotal,
          filesMissing: probe?.filesMissing,
          probeMs: probe?.probeMs,
          bytesTotal: probe?.bytesTotal ?? null,
          bytesMissing: probe?.bytesMissing ?? null,
          filesMissingAfter,
          downloadAttempts: attempt + 1,
          failureDetail,
          connectionType: currentConnectionType(),
          attemptDurationMs,
          lastProgressPercent: failedProgressPercent,
          batteryLevel: diagnostics.batteryLevel,
          isCharging: diagnostics.isCharging,
        });
      },
      wait,
      // Never gives up: returning false would mean rendering the stale bundle we have already
      // decided must not run.
      shouldContinue: () => true,
    });

    deps.setBootPhase('applying');
    await removeListener?.().catch(() => {});
    deps.setRetryAction(() => {
      void coveredReload();
    });
    deps.schedule(
      () => {
        liftCover('gate_apply_grace');
        deps.setGate((g) => ({
          percent: g?.percent ?? 0,
          isOffline: true,
          hasProgress: g?.hasProgress ?? false,
          hint: g?.hint ?? null,
        }));
      },
      APPLY_GRACE_MS,
      'apply_grace',
    );
  } catch (error) {
    // The decision was already taken: this update is MANDATORY.
    // Do NOT "recover" by dismissing the gate: that would boot the stale bundle we decided must not run.
    deps.reportError('OTA update gate failed', error);
    liftCover('gate_error');
    deps.setRetryAction(() => {
      void coveredReload();
    });
    deps.setGate((g) => ({
      percent: g?.percent ?? 0,
      isOffline: true,
      hasProgress: g?.hasProgress ?? false,
      hint: g?.hint ?? null,
    }));
    logAnalyticsEvent('app_update_failed', {
      failureReason: 'gate_failed',
      durationMs: deps.now() - startedAt,
      fromVersion: input.currentVersion,
      toVersion: decision.version,
    });
  } finally {
    cancelStallWatchdog();
    try {
      // A failed remove must not mask the gate outcome.
      void Promise.resolve(removeConnectionWatch?.()).catch(() => {});
    } catch {
      // Sync throw: same reason.
    }
  }

  return 'halt';
};

const emitUpdateCheckOutcome = (
  deps: Pick<BootSequenceDeps, 'logAnalyticsEvent' | 'now'>,
  decision: UpdateDecision,
  input: DecideUpdateInput,
  startedAt: number,
): void => {
  const { logAnalyticsEvent } = deps;
  if (decision.kind === 'check_failed') {
    logAnalyticsEvent('app_update_failed', {
      failureReason: 'check_failed',
      durationMs: deps.now() - startedAt,
      fromVersion: input.currentVersion,
    });
  } else if (decision.kind === 'below_floor') {
    // A refused rollback must be distinguishable from a healthy device — conflating the two into
    // `up_to_date` is what let the collapsed floor go unnoticed.
    logAnalyticsEvent('app_update_finished', {
      outcome: 'below_floor',
      floor: decision.floor,
      durationMs: deps.now() - startedAt,
      fromVersion: input.currentVersion,
    });
  } else if (decision.kind === 'checksum_missing') {
    logAnalyticsEvent('app_update_failed', {
      failureReason: 'checksum_missing',
      durationMs: deps.now() - startedAt,
      fromVersion: input.currentVersion,
      toVersion: decision.version,
    });
  } else if (decision.kind === 'no_url') {
    logAnalyticsEvent('app_update_finished', {
      outcome: 'no_url',
      durationMs: deps.now() - startedAt,
      fromVersion: input.currentVersion,
    });
  } else if (decision.kind === 'denied') {
    logAnalyticsEvent('app_update_finished', {
      outcome: 'denied',
      durationMs: deps.now() - startedAt,
      fromVersion: input.currentVersion,
    });
  } else if (decision.kind === 'download' || decision.kind === 'apply_installed') {
    // The camera fast-track's own case — see the doc comment above.
    logAnalyticsEvent('app_update_finished', {
      outcome: 'update_available',
      durationMs: deps.now() - startedAt,
      fromVersion: input.currentVersion,
      toVersion: decision.version,
    });
  } else {
    logAnalyticsEvent('app_update_finished', {
      outcome: 'up_to_date',
      durationMs: deps.now() - startedAt,
      fromVersion: input.currentVersion,
    });
  }
};

export const emitFastTrackUpdateTelemetry = (
  deps: Pick<
    BootSequenceDeps,
    'gatherUpdateInput' | 'setShouldUpdateNativeApp' | 'logAnalyticsEvent' | 'now' | 'reportError'
  >,
): void => {
  const { logAnalyticsEvent } = deps;
  const startedAt = deps.now();
  deps
    .gatherUpdateInput()
    .then((input) => {
      deps.setShouldUpdateNativeApp(computeShouldUpdateNativeApp(input.latest));
      emitUpdateCheckOutcome(deps, decideUpdate(input), input, startedAt);
    })
    .catch((error) => {
      deps.reportError('Camera fast-track update check failed', error);
      logAnalyticsEvent('app_update_failed', {
        failureReason: 'check_failed',
        durationMs: deps.now() - startedAt,
      });
    });
};

const raceUpdateCheck = (
  deps: Pick<BootSequenceDeps, 'schedule'>,
  work: Promise<DecideUpdateInput>,
): Promise<DecideUpdateInput> => {
  let cancel = () => {};
  const timeout = new Promise<never>((_resolve, reject) => {
    cancel = deps.schedule(
      () =>
        reject(
          new Error(`OTA update check timed out after ${UPDATE_CHECK_TIMEOUT_MS}ms`),
        ),
      UPDATE_CHECK_TIMEOUT_MS,
      'update_check',
    );
  });
  return Promise.race([
    work.then(
      (value) => {
        cancel();
        return value;
      },
      (error) => {
        cancel();
        throw error;
      },
    ),
    timeout,
  ]);
};

const runUpdateCheck = async (
  deps: BootSequenceDeps,
  state: SequenceState,
): Promise<BootOutcome> => {
  const { logAnalyticsEvent } = deps;
  deps.setBootPhase('checking');
  const startedAt = deps.now();

  // This try covers the PRE-DECISION section only, and its scope is load-bearing.
  let input: DecideUpdateInput;
  let decision: UpdateDecision;
  try {
    input = await raceUpdateCheck(deps, deps.gatherUpdateInput());
    deps.setShouldUpdateNativeApp(computeShouldUpdateNativeApp(input.latest));
    decision = decideUpdate(input);
  } catch (error) {
    deps.reportError('OTA update check failed', error);
    logAnalyticsEvent('app_update_failed', {
      failureReason: 'check_failed',
      durationMs: deps.now() - startedAt,
    });
    return 'continue';
  }

  if (decision.kind === 'download' || decision.kind === 'apply_installed') {
    return runGate(deps, state, decision, input, startedAt);
  }

  emitUpdateCheckOutcome(deps, decision, input, startedAt);
  return 'continue';
};

export const runBootSequence = async (
  deps: BootSequenceDeps,
): Promise<BootOutcome> => {
  const state: SequenceState = { gateRaised: false };
  try {
    return await runSteps(deps, state);
  } catch (error) {
    // `initApp` awaits this from a useLayoutEffect with no catch of its own, so an escaping
    // rejection would leave the app permanently blank.
    deps.reportError('Boot sequence failed', error);
    return state.gateRaised ? 'halt' : 'continue';
  }
};

const runSteps = async (
  deps: BootSequenceDeps,
  state: SequenceState,
): Promise<BootOutcome> => {
  deps.setBootPhase('pre_check');

  if ((await runCrashRecovery(deps)) === 'halt') return 'halt';

  // On native, Capgo manages the web bundle — a Service Worker would cause stale-cache conflicts
  // (chunk filenames have no content hash).
  if (deps.isNative) {
    deps.unregisterServiceWorkers();
  }

  await deps.showSplash().catch((error) => {
    deps.reportError('Error showing splash screen', error);
  });

  let localInitSucceeded = false;
  try {
    await deps.runLocalInit();
    localInitSucceeded = true;
  } catch (error) {
    deps.reportError('Error while initializing the app', error);
  }

  // The guard protects "can this bundle boot".
  clearCrashStateIfLocalInitSucceeded(deps, localInitSucceeded);

  if (!deps.isNative || deps.isDev) return 'continue';

  return runUpdateCheck(deps, state);
};
